/*
 * FactFindPrint
 * -------------
 * Standalone, read-only printable view of a v2 fact find. Used by both
 *
 *   /admin/factfindform/:applicationId/print
 *   /admin/factfindform/:applicationId/versions/:versionId/print
 *
 * - Loads the form data via /api/v2/factfindform/fetch (or /version when a
 *   versionId is in the route).
 * - Renders every section in an A4-friendly layout with sensible
 *   `page-break-inside: avoid` rules.
 * - Top-right "Print / Save as PDF" button calls window.print() - Chrome
 *   handles Save as PDF from the print dialog so we don't need any
 *   server-side PDF rendering.
 */
import React from 'react';
import { Helmet } from 'react-helmet';
import firebase from '../../Firebase.js';
import { fetchFactFindV2, fetchFactFindV2Version } from '../api';
import { EXPENSE_CATEGORIES } from '../steps/StepLivingExpenses';
import {
  WHY_OPTIONS,
  ASSIST_OPTIONS,
  PAYMENT_OPTIONS,
  IMPORTANT_FEATURES,
  FEATURE_ANSWER_OPTIONS,
} from '../steps/StepLoanRequirements';
import { applicantDisplayName } from '../steps.config';
import { countryName } from '../countries';

// Build a quick { key/value -> label } lookup from one of the option lists
// above so we can render full descriptive labels in the print view instead of
// the raw camel-case keys stored on the document.
const buildLookup = (options, keyField) =>
  (options || []).reduce((acc, opt) => {
    acc[opt[keyField]] = opt.label;
    return acc;
  }, {});

const WHY_LABELS = buildLookup(WHY_OPTIONS, 'key');
const ASSIST_LABELS = buildLookup(ASSIST_OPTIONS, 'key');
const PAYMENT_LABELS = buildLookup(PAYMENT_OPTIONS, 'key');
const FEATURE_ANSWER_LABELS = buildLookup(FEATURE_ANSWER_OPTIONS, 'value');

const truesAsLabels = (obj, lookup) => {
  const labels = Object.keys(obj || {})
    .filter((k) => obj[k] === true)
    .map((k) => lookup[k] || k.replace(/_/g, ' '));
  return labels.length ? labels.join(', ') : '-';
};

const formatMoney = (n) => {
  const v = parseFloat(n);
  if (isNaN(v)) return '-';
  return `$${v.toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;
};
const formatDate = (t) => {
  if (!t || !t.day || !t.month || !t.year) return '-';
  return `${String(t.day).padStart(2, '0')}/${String(t.month).padStart(2, '0')}/${t.year}`;
};
const formatDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return `${d.toLocaleDateString('en-AU')} ${d.toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
};
const blank = (v) => (v != null && String(v).trim() !== '' ? v : '-');
const yesNo = (v) => (v ? 'Yes' : 'No');

const Row = ({ label, value }) => (
  <tr>
    <td
      style={{
        padding: '4px 8px',
        width: '40%',
        color: '#6B7280',
        fontSize: '11px',
        verticalAlign: 'top',
      }}
    >
      {label}
    </td>
    <td
      style={{
        padding: '4px 8px',
        color: '#1F2937',
        fontSize: '12px',
        verticalAlign: 'top',
        wordBreak: 'break-word',
      }}
    >
      {value}
    </td>
  </tr>
);

// Major SectionHeading - colored banner that draws the eye. By default
// every section starts on a fresh page so the document is easy to scan
// when scrolling through a printed pack. Pass startOnNewPage={false} for
// the first section (or when chaining inside an existing section).
const SectionHeading = ({ title, subtitle, startOnNewPage }) => (
  <div
    style={{
      marginTop: startOnNewPage === false ? '24px' : '0',
      marginBottom: '12px',
      breakInside: 'avoid',
      breakBefore: startOnNewPage === false ? 'auto' : 'page',
      pageBreakBefore: startOnNewPage === false ? 'auto' : 'always',
    }}
  >
    <div
      style={{
        backgroundColor: '#5B3FE0',
        color: '#FFFFFF',
        padding: '12px 16px',
        borderRadius: '8px',
      }}
    >
      <h2
        style={{
          fontSize: '17px',
          fontWeight: 700,
          margin: 0,
          letterSpacing: '0.2px',
        }}
      >
        {title}
      </h2>
      {subtitle ? (
        <p
          style={{
            margin: '2px 0 0',
            fontSize: '11px',
            color: '#E2DCFE',
            fontWeight: 500,
          }}
        >
          {subtitle}
        </p>
      ) : null}
    </div>
  </div>
);

// Mid-level heading - subtle band so each item (Property 1, Loan 1, etc.)
// stands out without competing with the section banner.
const SubHeading = ({ title }) => (
  <h3
    style={{
      fontSize: '12px',
      fontWeight: 700,
      color: '#5B3FE0',
      backgroundColor: '#EFEAFE',
      borderLeft: '3px solid #7C5CFC',
      margin: '18px 0 0 0',
      padding: '8px 10px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    }}
  >
    {title}
  </h3>
);

const Table = ({ children }) => (
  <table
    style={{
      width: '100%',
      borderCollapse: 'collapse',
      borderTop: '1px solid #E5E7EB',
      borderBottom: '1px solid #E5E7EB',
    }}
  >
    <tbody>{children}</tbody>
  </table>
);

class FactFindPrint extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: '',
      data: null,
      meta: null, // application_id, completed, last_update, snapshot_at if version
    };
    this.didLoad = false;
    this.unsubscribeAuth = null;
  }

  // On a fresh tab Firebase auth often hasn't initialised yet at mount
  // time, which means the axios request interceptor in src/api/server.js
  // attaches no Bearer token and the server returns 401 Unauthorized -
  // which the shared interceptor then turns into a hard redirect to
  // /client. To avoid that, wait for onAuthStateChanged before firing the
  // API call.
  componentDidMount() {
    console.log('[FactFindPrint] mount - waiting for firebase auth');
    this.unsubscribeAuth = firebase.auth().onAuthStateChanged((user) => {
      if (this.didLoad) return; // only run once
      if (!user) {
        // No user signed in. Show an explicit error instead of letting the
        // axios interceptor punt us to /client silently.
        console.log('[FactFindPrint] no signed-in user');
        this.didLoad = true;
        this.setState({
          loading: false,
          error:
            'You need to be signed in to view this fact find. Please log in and try again.',
        });
        return;
      }
      console.log('[FactFindPrint] firebase user ready:', user.uid);
      this.didLoad = true;
      this.load();
    });
  }

  componentWillUnmount() {
    if (typeof this.unsubscribeAuth === 'function') {
      this.unsubscribeAuth();
      this.unsubscribeAuth = null;
    }
  }

  load = async () => {
    const m = this.props.match && this.props.match.params;
    const applicationId = m && m.applicationId;
    const versionId = m && m.versionId;
    console.log('[FactFindPrint] load', { applicationId, versionId });
    if (!applicationId) {
      this.setState({ loading: false, error: 'No application id in the URL.' });
      return;
    }
    try {
      if (versionId) {
        const res = await fetchFactFindV2Version(applicationId, versionId);
        console.log('[FactFindPrint] version response', res);
        if (!res || !res.ok || !res.version) {
          this.setState({
            loading: false,
            error:
              (res && res.error) || 'Version not found for this application.',
          });
          return;
        }
        this.setState({
          loading: false,
          data: res.version.data || {},
          meta: {
            applicationId,
            versionId,
            snapshot_at: res.version.snapshot_at,
            snapshot_reason: res.version.snapshot_reason,
            completed: res.version.completed,
            applicants_at_snapshot: res.version.applicants_at_snapshot || [],
          },
        });
      } else {
        const res = await fetchFactFindV2(applicationId);
        console.log('[FactFindPrint] fetch response', res);
        if (!res || !res.ok) {
          this.setState({
            loading: false,
            error:
              (res && res.error) ||
              'We couldn\'t load the fact find. The customer may not have started it yet.',
          });
          return;
        }
        // Render even when the customer hasn't finished - empty sections
        // just appear as blank rows. This is the expected admin behaviour.
        this.setState({
          loading: false,
          data: res.data || {},
          meta: {
            applicationId,
            completed: res.completed,
            last_update: res.last_update,
          },
        });
      }
    } catch (err) {
      console.log('[FactFindPrint] load threw', err);
      this.setState({
        loading: false,
        error:
          'Failed to load the fact find. Check the browser console for details.',
      });
    }
  };

  // Per-address block. Renders one of two shapes depending on how the
  // address was captured:
  //
  // 1. "Split" form (separate rows per field) - used when the customer
  //    picked the address via Google Places autocomplete OR hand-edited
  //    the unit / street_number fields. We key off `unit_number` or
  //    `street_number` because those two only get populated by a real
  //    split (auto-fill or manual entry); they're never set by the
  //    legacy migration which only had a single street string.
  //
  // 2. "Combined" form (single Address row) - used for migrated v1
  //    addresses and for any property where the broker only captured a
  //    single address string. We compose the row from whatever fragments
  //    we have so the broker sees the real address rather than a column
  //    of dashes.
  //
  // `country` is intentionally excluded from the split-detection
  // because the migration always defaults it to 'au'; we surface it
  // separately only when it differs from the default.
  renderAddressRows(addr) {
    const a = addr || {};
    const hasSplitParts = !!(a.unit_number || a.street_number);
    if (hasSplitParts) {
      return (
        <>
          <Row label="Unit / apt" value={blank(a.unit_number)} />
          <Row label="Street number" value={blank(a.street_number)} />
          <Row label="Street name" value={blank(a.street_name)} />
          <Row label="Suburb / city" value={blank(a.suburb)} />
          <Row label="State / province" value={blank(a.state)} />
          <Row label="Postcode" value={blank(a.postcode)} />
          <Row label="Country" value={a.country ? countryName(a.country) : '-'} />
        </>
      );
    }
    // Build a single combined string from the best available fields.
    // Prefer the explicit `address` (on properties) or `formatted`
    // (autocomplete) string when present; otherwise stitch together
    // whatever pieces we have.
    const stitched = [
      a.street_name || a.street || '',
      [a.suburb, a.state, a.postcode].filter(Boolean).join(' '),
    ].filter((s) => s && s.trim().length > 0).join(', ');
    const combined = a.address || a.formatted || stitched;
    const countryLabel =
      a.country && a.country.toLowerCase() !== 'au'
        ? countryName(a.country)
        : '';
    return (
      <>
        <Row label="Address" value={blank(combined)} />
        {countryLabel ? <Row label="Country" value={countryLabel} /> : null}
      </>
    );
  }

  // Render a single employment role (current or previous) as its own
  // SubHeading + Table block so every field gets its own line.
  renderEmploymentRole(job, title, opts) {
    const j = job || {};
    const includeEnd = opts && opts.includeEnd;
    return (
      <div style={{ breakInside: 'avoid' }}>
        <SubHeading title={title} />
        <Table>
          <Row label="Position / job title" value={blank(j.position)} />
          <Row label="Employer name" value={blank(j.employer_name)} />
          <Row label="Employer phone" value={blank(j.employer_phone)} />
          <Row label="Employer address" value={blank(j.employer_address)} />
          <Row label="Employment status" value={blank(j.employment_status)} />
          <Row
            label="Gross income (annual, pre-tax)"
            value={`${formatMoney(j.gross_income)} / year`}
          />
          <Row label="Start date" value={formatDate(j.start_date)} />
          {includeEnd ? (
            <Row label="End date" value={formatDate(j.end_date)} />
          ) : null}
        </Table>
      </div>
    );
  }

  renderApplicantBlock(applicant, index) {
    const display = applicantDisplayName(applicant, index);
    const a = applicant || {};
    const address = a.address || { current: {}, previous: [] };
    const employment = a.employment || { current: {}, previous: [] };
    const current = address.current || {};
    const empCurrent = employment.current || {};

    return (
      <div key={a.id || index} style={{ marginBottom: '14px' }}>
        <SectionHeading
          title={`Applicant ${index + 1} - ${display}`}
          subtitle={a.email || ''}
          startOnNewPage={index !== 0}
        />

        <div style={{ breakInside: 'avoid' }}>
          <SubHeading title="Personal details" />
          <Table>
            <Row label="Title" value={blank(a.title)} />
            <Row label="Given name" value={blank(a.given_name)} />
            <Row label="Middle name" value={blank(a.middle_name)} />
            <Row label="Family name" value={blank(a.family_name)} />
            <Row label="Maiden name" value={blank(a.maiden_name)} />
            <Row label="Email" value={blank(a.email)} />
            <Row label="Mobile" value={blank(a.mobile)} />
            <Row label="Home phone" value={blank(a.home_phone)} />
            <Row label="Work phone" value={blank(a.work_phone)} />
            <Row label="Date of birth" value={formatDate(a.dob)} />
            <Row label="Gender" value={blank(a.gender)} />
            <Row label="Marital status" value={blank(a.martial_status)} />
            <Row label="Residency" value={blank(a.resident_status)} />
            {a.resident_status === 'Visa' ? (
              <Row label="Visa details" value={blank(a.resident_visa_details)} />
            ) : null}
          </Table>
        </div>

        {(a.dependants || []).length > 0 ? (
          <div style={{ breakInside: 'avoid' }}>
            <SubHeading title="Dependents" />
            <Table>
              {(a.dependants || []).map((d, i) => (
                <React.Fragment key={d.id || i}>
                  <Row label={`Dependent ${i + 1} - name`} value={blank(d.name)} />
                  <Row label={`Dependent ${i + 1} - age`} value={blank(d.age)} />
                  {Array.isArray(d.sharedWith) && d.sharedWith.length ? (
                    <Row
                      label={`Dependent ${i + 1} - shared with`}
                      value={`${d.sharedWith.length} other applicant${d.sharedWith.length === 1 ? '' : 's'}`}
                    />
                  ) : null}
                </React.Fragment>
              ))}
            </Table>
          </div>
        ) : null}

        {/* Current address - every field on its own row. */}
        <div style={{ breakInside: 'avoid' }}>
          <SubHeading title="Current address" />
          <Table>
            {this.renderAddressRows(current)}
            <Row label="Living arrangement" value={blank(current.address_type)} />
            {['Renting', 'Boarding'].includes(current.address_type) ? (
              <Row
                label="Rent / board (per month)"
                value={`${formatMoney(current.address_amount)} / month`}
              />
            ) : null}
            {current.address_type === 'Other' ? (
              <Row label="Arrangement (other)" value={blank(current.other_text)} />
            ) : null}
            <Row label="Moved in" value={formatDate(current.moved_in)} />
          </Table>
        </div>

        {/* Previous addresses - each one in its own block. */}
        {(address.previous || []).map((p, i) => (
          <div key={`prev-addr-${i}`} style={{ breakInside: 'avoid' }}>
            <SubHeading title={`Previous address ${i + 1}`} />
            <Table>
              {this.renderAddressRows(p)}
              <Row label="Living arrangement" value={blank(p.address_type)} />
              {['Renting', 'Boarding'].includes(p.address_type) ? (
                <Row
                  label="Rent / board (per month)"
                  value={`${formatMoney(p.address_amount)} / month`}
                />
              ) : null}
              {p.address_type === 'Other' ? (
                <Row label="Arrangement (other)" value={blank(p.other_text)} />
              ) : null}
              <Row label="Moved in" value={formatDate(p.moved_in)} />
              <Row label="Moved out" value={formatDate(p.moved_out)} />
            </Table>
          </div>
        ))}

        {/* Employment - one block per role. */}
        {this.renderEmploymentRole(empCurrent, 'Current employment', { includeEnd: false })}
        {(employment.previous || []).map((p, i) =>
          this.renderEmploymentRole(p, `Previous employment ${i + 1}`, { includeEnd: true })
        )}

        {a.other_income_details ? (
          <div style={{ breakInside: 'avoid' }}>
            <SubHeading title="Other income" />
            <div
              style={{
                padding: '6px 8px',
                fontSize: '12px',
                color: '#1F2937',
                whiteSpace: 'pre-wrap',
                borderTop: '1px solid #E5E7EB',
                borderBottom: '1px solid #E5E7EB',
              }}
            >
              {a.other_income_details}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  renderExpenses(expenses) {
    let total = 0;
    EXPENSE_CATEGORIES.forEach((c) => {
      const v = parseFloat(expenses[c.key]);
      if (!isNaN(v)) total += v;
    });
    const otherAmt = parseFloat(expenses.other_amount);
    if (!isNaN(otherAmt)) total += otherAmt;

    return (
      <div style={{ breakInside: 'avoid' }}>
        <SectionHeading
          title="Monthly living expenses"
          subtitle="Household totals - excluding the new loan repayment."
        />
        <Table>
          {EXPENSE_CATEGORIES.map((c) => (
            <Row key={c.key} label={c.label} value={formatMoney(expenses[c.key])} />
          ))}
          {expenses.other ? (
            <Row label={`Other (${expenses.other})`} value={formatMoney(expenses.other_amount)} />
          ) : null}
          <tr>
            <td
              style={{
                padding: '8px 8px',
                fontSize: '13px',
                fontWeight: 700,
                color: '#1F2937',
                borderTop: '2px solid #1F2937',
              }}
            >
              Total
            </td>
            <td
              style={{
                padding: '8px 8px',
                fontSize: '13px',
                fontWeight: 700,
                color: '#5B3FE0',
                borderTop: '2px solid #1F2937',
              }}
            >
              {formatMoney(total)} / month
            </td>
          </tr>
        </Table>
      </div>
    );
  }

  renderLiabilities(liabilities, properties, applicantsById) {
    const loans = Object.values(liabilities.loans || {});
    const cards = Object.values(liabilities.credit_cards || {});
    const leases = Object.values(liabilities.leases || {});
    const ownersOf = (item) =>
      Object.keys(item.ownership || {})
        .filter((id) => item.ownership[id])
        .map((id) => (applicantsById[id] && applicantsById[id].name) || id)
        .join(', ') || '-';

    // Build a reverse lookup so each loan card can show which property
    // it's secured against (cross-reference to the merged property view).
    const loanIdToProperty = {};
    Object.values(properties || {}).forEach((p) => {
      (Array.isArray(p.linked_loan_ids) ? p.linked_loan_ids : []).forEach((id) => {
        loanIdToProperty[id] = p;
      });
    });
    const propertyLabel = (p) => {
      if (!p) return null;
      if (p.address) return p.address;
      const street = [p.street_number, p.street_name].filter(Boolean).join(' ');
      const tail = [p.suburb, p.state, p.postcode].filter(Boolean).join(', ');
      return [street, tail].filter(Boolean).join(', ');
    };

    const noneRecorded = (
      <p style={{ fontSize: '12px', color: '#6B7280', margin: '4px 0' }}>None recorded.</p>
    );

    // Only show loans that are NOT linked to a property here - linked
    // loans are already rendered inline under their property in the
    // Assets section, so this section just covers the unsecured /
    // standalone ones.
    const unsecuredLoans = loans.filter((l) => !loanIdToProperty[l.id]);

    return (
      <div>
        <SectionHeading title="Liabilities & debts" />

        {/* Loans (unsecured only - secured loans live with the property). */}
        {loans.length === 0 ? (
          <div style={{ breakInside: 'avoid' }}>
            <SubHeading title="Loans" />
            {noneRecorded}
          </div>
        ) : unsecuredLoans.length === 0 ? (
          <div style={{ breakInside: 'avoid' }}>
            <SubHeading title="Loans" />
            <p style={{ fontSize: '12px', color: '#6B7280', margin: '4px 0' }}>
              All declared loans are secured against properties and are listed
              with the property details in the Assets section.
            </p>
          </div>
        ) : (
          unsecuredLoans.map((l, i) => (
            <div key={l.id || i} style={{ breakInside: 'avoid' }}>
              <SubHeading
                title={`Loan ${i + 1}${l.bank_name ? ` - ${l.bank_name}` : ''}`}
              />
              <Table>
                <Row label="Lender / bank" value={blank(l.bank_name)} />
                <Row label="Secured against" value="Unsecured / no property linked" />
                <Row label="Amount owing" value={formatMoney(l.owing)} />
                {l.interest_rate ? (
                  <Row label="Interest rate" value={`${l.interest_rate}% p.a.`} />
                ) : null}
                <Row
                  label="Monthly repayment"
                  value={`${formatMoney(l.repayment)} / month`}
                />
                <Row label="Owned by" value={ownersOf(l)} />
                {l.held_other_text ? (
                  <Row label="Other ownership" value={l.held_other_text} />
                ) : null}
              </Table>
            </div>
          ))
        )}

        {/* Credit cards - one block per card. */}
        {cards.length === 0
          ? (
            <div style={{ breakInside: 'avoid' }}>
              <SubHeading title="Credit cards" />
              {noneRecorded}
            </div>
          )
          : cards.map((c, i) => (
            <div key={c.id || i} style={{ breakInside: 'avoid' }}>
              <SubHeading
                title={`Credit card ${i + 1}${c.bank_name ? ` - ${c.bank_name}` : ''}`}
              />
              <Table>
                <Row label="Provider / bank" value={blank(c.bank_name)} />
                <Row label="Credit limit" value={formatMoney(c.limit)} />
                <Row label="Amount owing" value={formatMoney(c.owing)} />
                <Row label="Owned by" value={ownersOf(c)} />
                {c.held_other_text ? (
                  <Row label="Other ownership" value={c.held_other_text} />
                ) : null}
              </Table>
            </div>
          ))}

        {/* Leases - one block per lease. */}
        {leases.length === 0
          ? (
            <div style={{ breakInside: 'avoid' }}>
              <SubHeading title="Leases" />
              {noneRecorded}
            </div>
          )
          : leases.map((l, i) => (
            <div key={l.id || i} style={{ breakInside: 'avoid' }}>
              <SubHeading
                title={`Lease ${i + 1}${l.lessor ? ` - ${l.lessor}` : ''}`}
              />
              <Table>
                <Row label="Lessor / provider" value={blank(l.lessor)} />
                <Row label="Lease type" value={blank(l.lease_type)} />
                <Row
                  label="Monthly payment"
                  value={`${formatMoney(l.monthly_payment)} / month`}
                />
                <Row label="Lease end date" value={formatDate(l.end_date)} />
                <Row label="Owned by" value={ownersOf(l)} />
                {l.held_other_text ? (
                  <Row label="Other ownership" value={l.held_other_text} />
                ) : null}
              </Table>
            </div>
          ))}
      </div>
    );
  }

  renderAssets(assets, liabilities, applicantsById) {
    const properties = Object.values(assets.properties || {});
    const savings = Object.values(assets.savings || {});
    const shares = Object.values(assets.shares || {});
    const supers = Object.values(assets.supers || {});
    const vehicles = Object.values(assets.vehicles || {});
    const others = Object.values(assets.other_assets || {});
    const loanLookup = liabilities && liabilities.loans ? liabilities.loans : {};
    const ownersOf = (item) =>
      Object.keys(item.ownership || {})
        .filter((id) => item.ownership[id])
        .map((id) => (applicantsById[id] && applicantsById[id].name) || id)
        .join(', ') || '-';
    const loanLabel = (id, i) => {
      const l = loanLookup[id];
      if (!l) return `Loan ${i + 1}`;
      return l.bank_name || `Loan ${i + 1}`;
    };

    const noneRecorded = (heading) => (
      <div style={{ breakInside: 'avoid' }}>
        <SubHeading title={heading} />
        <p style={{ fontSize: '12px', color: '#6B7280', margin: '4px 0' }}>None recorded.</p>
      </div>
    );

    return (
      <div>
        <SectionHeading title="Assets" />

        {/* Real estate - one block per property, with split address rows
            and any linked loans inlined so the broker can see at a glance
            which loan(s) sit against which property. */}
        {properties.length === 0
          ? noneRecorded('Real estate')
          : properties.map((p, i) => {
            const linkedLoanIds = Array.isArray(p.linked_loan_ids)
              ? p.linked_loan_ids
              : [];
            const linkedLoans = linkedLoanIds
              .map((id) => loanLookup[id])
              .filter(Boolean);
            return (
              <div key={`prop-${i}`} style={{ breakInside: 'avoid' }}>
                <SubHeading
                  title={`Property ${i + 1}${p.address ? ` - ${p.address}` : ''}`}
                />
                <Table>
                  {this.renderAddressRows(p)}
                  <Row label="Use" value={blank(p.property_use)} />
                  <Row label="Estimated value" value={formatMoney(p.value)} />
                  {p.rental_income ? (
                    <Row
                      label="Rental income"
                      value={`${formatMoney(p.rental_income)} / week`}
                    />
                  ) : null}
                  <Row label="Owned by" value={ownersOf(p)} />
                  {p.held_other_text ? (
                    <Row label="Other ownership" value={p.held_other_text} />
                  ) : null}
                  <Row
                    label="Loans secured by this property"
                    value={
                      linkedLoanIds.length === 0
                        ? 'None'
                        : linkedLoanIds
                            .map((id, idx) => loanLabel(id, idx))
                            .join(', ')
                    }
                  />
                </Table>

                {/* Inline detail card for each loan linked to this
                    property so the broker doesn't have to flip between
                    sections. */}
                {linkedLoans.map((l, idx) => (
                  <div
                    key={`prop-${i}-loan-${l.id || idx}`}
                    style={{
                      marginTop: '10px',
                      marginLeft: '12px',
                      borderLeft: '3px solid #7C5CFC',
                      paddingLeft: '12px',
                      breakInside: 'avoid',
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                        color: '#5B3FE0',
                      }}
                    >
                      Secured loan {idx + 1} -{' '}
                      {l.bank_name || `Loan ${idx + 1}`}
                    </p>
                    <Table>
                      <Row label="Lender / bank" value={blank(l.bank_name)} />
                      <Row label="Amount owing" value={formatMoney(l.owing)} />
                      {l.interest_rate ? (
                        <Row
                          label="Interest rate"
                          value={`${l.interest_rate}% p.a.`}
                        />
                      ) : null}
                      <Row
                        label="Monthly repayment"
                        value={`${formatMoney(l.repayment)} / month`}
                      />
                      <Row label="Owned by" value={ownersOf(l)} />
                    </Table>
                  </div>
                ))}
              </div>
            );
          })}

        <div style={{ breakInside: 'avoid' }}>
          <SubHeading title="Home contents" />
          <Table>
            <Row
              label="Total value of household contents"
              value={formatMoney(assets.home_contents_value)}
            />
          </Table>
        </div>

        {/* Savings - one block per account. */}
        {savings.length === 0
          ? noneRecorded('Savings accounts')
          : savings.map((s, i) => (
            <div key={`sav-${i}`} style={{ breakInside: 'avoid' }}>
              <SubHeading
                title={`Savings ${i + 1}${s.name ? ` - ${s.name}` : ''}`}
              />
              <Table>
                <Row label="Account / institution" value={blank(s.name)} />
                <Row label="Current balance" value={formatMoney(s.value)} />
                <Row label="Owned by" value={ownersOf(s)} />
                {s.held_other_text ? (
                  <Row label="Other ownership" value={s.held_other_text} />
                ) : null}
              </Table>
            </div>
          ))}

        {/* Shares & managed funds. */}
        {shares.length === 0
          ? noneRecorded('Shares & managed funds')
          : shares.map((s, i) => (
            <div key={`sh-${i}`} style={{ breakInside: 'avoid' }}>
              <SubHeading
                title={`Holding ${i + 1}${s.name ? ` - ${s.name}` : ''}`}
              />
              <Table>
                <Row label="Share / fund name" value={blank(s.name)} />
                <Row label="Current value" value={formatMoney(s.value)} />
                <Row label="Owned by" value={ownersOf(s)} />
                {s.held_other_text ? (
                  <Row label="Other ownership" value={s.held_other_text} />
                ) : null}
              </Table>
            </div>
          ))}

        {/* Superannuation. */}
        {supers.length === 0
          ? noneRecorded('Superannuation')
          : supers.map((s, i) => (
            <div key={`sup-${i}`} style={{ breakInside: 'avoid' }}>
              <SubHeading
                title={`Super ${i + 1}${s.name ? ` - ${s.name}` : ''}`}
              />
              <Table>
                <Row label="Fund name" value={blank(s.name)} />
                <Row label="Current balance" value={formatMoney(s.value)} />
                <Row label="Whose super is this?" value={ownersOf(s)} />
                {s.held_other_text ? (
                  <Row label="Other ownership" value={s.held_other_text} />
                ) : null}
              </Table>
            </div>
          ))}

        {/* Vehicles. */}
        {vehicles.length === 0
          ? noneRecorded('Vehicles')
          : vehicles.map((v, i) => (
            <div key={`veh-${i}`} style={{ breakInside: 'avoid' }}>
              <SubHeading
                title={`Vehicle ${i + 1}${[v.year, v.make, v.model].filter(Boolean).length ? ` - ${[v.year, v.make, v.model].filter(Boolean).join(' ')}` : ''}`}
              />
              <Table>
                <Row label="Make" value={blank(v.make)} />
                <Row label="Model" value={blank(v.model)} />
                <Row label="Year" value={blank(v.year)} />
                <Row label="Estimated value" value={formatMoney(v.value)} />
                <Row label="Owned by" value={ownersOf(v)} />
                {v.held_other_text ? (
                  <Row label="Other ownership" value={v.held_other_text} />
                ) : null}
              </Table>
            </div>
          ))}

        {/* Other assets. */}
        {others.length === 0
          ? noneRecorded('Other assets')
          : others.map((o, i) => (
            <div key={`oth-${i}`} style={{ breakInside: 'avoid' }}>
              <SubHeading
                title={`Other asset ${i + 1}${o.name ? ` - ${o.name}` : ''}`}
              />
              <Table>
                <Row label="Asset name" value={blank(o.name)} />
                <Row label="Estimated value" value={formatMoney(o.value)} />
                <Row label="Owned by" value={ownersOf(o)} />
                {o.held_other_text ? (
                  <Row label="Other ownership" value={o.held_other_text} />
                ) : null}
              </Table>
            </div>
          ))}
      </div>
    );
  }

  renderLoanRequirements(lr) {
    const features = lr.important_features || {};
    return (
      <div>
        <SectionHeading title="Loan requirements" />
        <div style={{ breakInside: 'avoid' }}>
          <Table>
            <Row
              label="Why are you using our service?"
              value={truesAsLabels(lr.why, WHY_LABELS)}
            />
            <Row
              label="What can we assist you with?"
              value={truesAsLabels(lr.what_can_assist, ASSIST_LABELS)}
            />
            <Row
              label="How will you repay the loan?"
              value={truesAsLabels(lr.payment_plan, PAYMENT_LABELS)}
            />
            {lr.payment_plan && lr.payment_plan.other_check ? (
              <Row
                label="Repayment plan - other (please specify)"
                value={blank((lr.payment_plan || {}).other)}
              />
            ) : null}
            <Row
              label="Preferred loan term"
              value={
                lr.loan_term === 'other'
                  ? `${blank(lr.loan_term_other)} years`
                  : lr.loan_term
                    ? `${lr.loan_term} years`
                    : '-'
              }
            />
            <Row
              label="Expected financial changes"
              value={blank(lr.changes_in_financial_circum)}
            />
            {lr.changes_in_financial_circum === 'Yes' ? (
              <Row
                label="Expected financial changes - details"
                value={blank(lr.changes_in_financial_circum_yes)}
              />
            ) : null}
            <Row label="Repayment type" value={blank(lr.loan_repayment_type)} />
            {lr.loan_repayment_type === 'Interest Only' ? (
              <Row label="Interest only term" value={blank(lr.interest_only_term)} />
            ) : null}
            <Row label="Rate type" value={blank(lr.rate_type)} />
          </Table>
        </div>
        <div style={{ breakInside: 'avoid' }}>
          <SubHeading title="Important features" />
          <Table>
            {IMPORTANT_FEATURES.map((f) => {
              const answerRaw = (features[f.key] || {}).answer;
              const answerLabel = answerRaw
                ? FEATURE_ANSWER_LABELS[answerRaw] || answerRaw
                : '-';
              return <Row key={f.key} label={f.label} value={answerLabel} />;
            })}
            {lr.important_features_other ? (
              <Row
                label="Other features requested"
                value={lr.important_features_other}
              />
            ) : null}
          </Table>
        </div>
      </div>
    );
  }

  render() {
    // Build the document title from applicant names so saved PDFs have a
    // useful filename like "Fact Find Form - Bobby Black & Nigeria Runner".
    const data = this.state.data || {};
    const applicants = Array.isArray(data.applicants) ? data.applicants : [];
    const applicantNames = applicants.map((a, i) => applicantDisplayName(a, i));
    let titleLine;
    if (applicantNames.length === 0) {
      titleLine = 'Unnamed';
    } else if (applicantNames.length === 1) {
      titleLine = applicantNames[0];
    } else {
      const last = applicantNames[applicantNames.length - 1];
      const rest = applicantNames.slice(0, -1);
      titleLine = `${rest.join(', ')} & ${last}`;
    }
    const documentTitle =
      applicantNames.length === 0
        ? 'Fact Find Form'
        : `Fact Find Form - ${titleLine}`;

    const helmet = (
      <Helmet>
        {/*
          Toggle the modern-page body styles via <html className>. See
          client/src/index.css ".lg-modern-page" rules - they override
          the legacy /css/loader.css (which forces html/body to 100px),
          hide the .full-loader overlay, and set the modern background +
          font. Using <html> attributes (instead of an inline <style>
          tag) ensures react-helmet@6.1 correctly REMOVES them on
          unmount so they don't leak into /admin pages.
          The inline <style> below keeps only the PRINT-specific rules
          (page size + paint overrides while printing) which are not
          needed for any other route.
        */}
        <html className="lg-modern-page" />
        <title>{documentTitle}</title>
        <style>{`
          @page { size: A4; margin: 14mm; }
          @media print {
            body { background-color: #FFFFFF !important; }
            .no-print { display: none !important; }
            .print-page { box-shadow: none !important; border: none !important; }
          }
        `}</style>
      </Helmet>
    );

    if (this.state.loading) {
      return (
        <div>
          {helmet}
          <div style={{ padding: '32px', textAlign: 'center', color: '#6B7280' }}>
            Loading fact find...
          </div>
        </div>
      );
    }
    if (this.state.error) {
      return (
        <div>
          {helmet}
          <div style={{ padding: '32px', textAlign: 'center', color: '#DC2626' }}>
            {this.state.error}
          </div>
        </div>
      );
    }

    const applicantsById = applicants.reduce((acc, a, idx) => {
      acc[a.id] = { ...a, name: applicantDisplayName(a, idx) };
      return acc;
    }, {});
    const liabilities = data.liabilities || { loans: {}, credit_cards: {} };
    const meta = this.state.meta || {};

    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#F5F6FA', padding: '20px 0' }}>
        {helmet}

        {/* Floating print button - hidden when printing. */}
        <div
          className="no-print"
          style={{
            position: 'fixed',
            top: '16px',
            right: '16px',
            zIndex: 50,
            display: 'flex',
            gap: '8px',
          }}
        >
          <button
            type="button"
            onClick={() => window.print()}
            style={{
              backgroundColor: '#7C5CFC',
              color: '#FFFFFF',
              padding: '8px 14px',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            Print / Save as PDF
          </button>
        </div>

        <div
          className="print-page"
          style={{
            maxWidth: '820px',
            margin: '0 auto',
            padding: '32px 36px',
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              borderBottom: '2px solid #1F2937',
              paddingBottom: '12px',
              marginBottom: '4px',
            }}
          >
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: '10px',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  color: '#5B3FE0',
                  fontWeight: 700,
                }}
              >
                LoanGateway - Fact Find
              </p>
              <h1
                style={{
                  margin: '4px 0 0',
                  fontSize: '20px',
                  color: '#1F2937',
                  fontWeight: 700,
                }}
              >
                {titleLine}
              </h1>
              <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#6B7280' }}>
                Application{' '}
                <a
                  href={`/admin/application/${meta.applicationId}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{
                    color: '#5B3FE0',
                    fontWeight: 600,
                    textDecoration: 'underline',
                  }}
                >
                  {meta.applicationId}
                </a>
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              {meta.versionId ? (
                <>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '10px',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      color: '#6B7280',
                    }}
                  >
                    Version snapshot
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#1F2937', fontWeight: 600 }}>
                    {formatDateTime(meta.snapshot_at)}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6B7280' }}>
                    Reason: {meta.snapshot_reason || '-'}
                  </p>
                </>
              ) : (
                <>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '10px',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      color: '#6B7280',
                    }}
                  >
                    Status
                  </p>
                  <p
                    style={{
                      margin: '2px 0 0',
                      fontSize: '12px',
                      color: meta.completed ? '#166534' : '#92400E',
                      fontWeight: 600,
                    }}
                  >
                    {meta.completed ? 'Submitted' : 'In progress'}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6B7280' }}>
                    Last update {formatDateTime(meta.last_update)}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Applicants */}
          {applicants.map((a, i) => this.renderApplicantBlock(a, i))}

          {/* Joint sections */}
          {this.renderExpenses(data.expenses || {})}
          {this.renderLiabilities(liabilities, (data.assets || {}).properties, applicantsById)}
          {this.renderAssets(data.assets || {}, liabilities, applicantsById)}
          {this.renderLoanRequirements(data.loan_requirements || {})}

          <div
            style={{
              marginTop: '32px',
              paddingTop: '12px',
              borderTop: '1px solid #E5E7EB',
              fontSize: '10px',
              color: '#6B7280',
              textAlign: 'center',
            }}
          >
            LoanGateway fact find {meta.versionId ? 'version' : 'current state'} - printed {new Date().toLocaleString('en-AU')}.
          </div>
        </div>
      </div>
    );
  }
}

export default FactFindPrint;
